import { useCallback, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function readSSE(response, onEvent) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop();
    for (const part of parts) {
      const line = part.trim();
      if (line.startsWith("data: ")) onEvent(JSON.parse(line.slice(6)));
    }
  }
}

export function useAlterEgoChat() {
  const [decision, setDecision] = useState("");
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const sessionRef = useRef(null);
  const abortRef = useRef(null);
  const lastRequestRef = useRef(null);

  const appendDelta = (content) =>
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      next[next.length - 1] = { ...last, text: last.text + content };
      return next;
    });

  const stream = useCallback(async (url, body) => {
    setIsStreaming(true);
    setError(null);
    lastRequestRef.current = { url, body };
    const controller = new AbortController();
    abortRef.current = controller;
    setMessages((prev) => [...prev, { role: "alter", text: "" }]);
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Request failed (${res.status})`);
      }
      await readSSE(res, (ev) => {
        if (ev.type === "session") sessionRef.current = ev.session_id;
        else if (ev.type === "delta") appendDelta(ev.content);
        else if (ev.type === "error") throw new Error(ev.message);
      });
      lastRequestRef.current = null;
    } catch (e) {
      if (e.name === "AbortError") {
        lastRequestRef.current = null;
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (!last || last.role !== "alter") return prev;
          return last.text === "" ? prev.slice(0, -1) : [...prev.slice(0, -1), { ...last, stopped: true }];
        });
      } else {
        setError(e.message || "Something went wrong");
        setMessages((prev) => (prev[prev.length - 1]?.role === "alter" ? prev.slice(0, -1) : prev));
      }
    } finally {
      abortRef.current = null;
      setIsStreaming(false);
    }
  }, []);

  const stopReply = useCallback(() => abortRef.current?.abort(), []);

  const retry = useCallback(() => {
    const req = lastRequestRef.current;
    if (req && !abortRef.current) stream(req.url, req.body);
  }, [stream]);

  const startChat = useCallback(
    async (text) => {
      setDecision(text);
      setMessages([]);
      sessionRef.current = null;
      await stream(`${API}/chat/start`, { decision: text });
    },
    [stream]
  );

  const sendMessage = useCallback(
    async (text) => {
      if (!sessionRef.current || isStreaming) return;
      setMessages((prev) => [...prev, { role: "user", text }]);
      await stream(`${API}/chat/message`, { session_id: sessionRef.current, message: text });
    },
    [stream, isStreaming]
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    if (sessionRef.current) fetch(`${API}/chat/${sessionRef.current}`, { method: "DELETE" }).catch(() => {});
    sessionRef.current = null;
    lastRequestRef.current = null;
    setDecision("");
    setMessages([]);
    setError(null);
    setIsStreaming(false);
  }, []);

  return { decision, messages, isStreaming, error, startChat, sendMessage, stopReply, retry, reset };
}
